/**
 * @file WriteoffReservedDialog.tsx
 * @description Подтверждение списания зарезервированного товара (phase19.13 этап 0).
 *
 * Сервер отвечает 409 `writeoff_reserved_stock`, если после списания остаток
 * не покроет активные резервы заказов. Кладовщик должен видеть, по каким
 * фасовкам и насколько расходится, и осознанно подтвердить: заказы придётся
 * собрать позже или отменить. Подтверждение = повтор commit с
 * `confirmReservedWriteoff: true`.
 *
 * Mobile-first: на < sm диалог во весь экран, таблица превращается в карточки,
 * кнопки ≥56px (warehouse-UI).
 */

import { AlertTriangle, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import type { WriteoffReservedConflict } from "../../lib/api-wms";

interface Props {
  open: boolean;
  conflicts: WriteoffReservedConflict[];
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Количества приходят числами — печатаем без «хвоста» 10.000. */
function formatQty(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString("ru-RU") : "—";
}

export function WriteoffReservedDialog({
  open,
  conflicts,
  submitting = false,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => (!v ? onCancel() : null)}>
      <AlertDialogContent fullScreen className="max-w-2xl gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 shrink-0 text-destructive" />
            Товар зарезервирован под заказы
          </AlertDialogTitle>
          <AlertDialogDescription>
            По этим фасовкам остатка после списания не хватит на уже собранные
            резервы.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Mobile — карточки */}
        <ul className="flex flex-col gap-2 sm:hidden">
          {conflicts.map((c) => (
            <li
              key={c.skuId}
              className="flex flex-col gap-1 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
            >
              <span className="text-sm font-medium">{c.skuLabel}</span>
              <span className="font-mono text-xs text-on-surface-variant">
                {c.skuCode}
              </span>
              <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                <QtyCell label="Списываем" value={c.writeoffQuantity} tone="danger" />
                <QtyCell label="На складе" value={c.physicalQuantity} />
                <QtyCell label="В резерве" value={c.reservedQuantity} tone="warn" />
              </div>
            </li>
          ))}
        </ul>

        {/* Desktop — таблица */}
        {/* overflow-x-auto: широкая таблица скроллится внутри контейнера, страница — нет */}
        <div className="hidden overflow-x-auto rounded-lg border border-on-surface/8 sm:block">
          <table className="w-full text-sm">
            <thead className="bg-on-surface/5 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Позиция</th>
                <th className="px-3 py-2 text-right font-semibold">Списываем</th>
                <th className="px-3 py-2 text-right font-semibold">Физический остаток</th>
                <th className="px-3 py-2 text-right font-semibold">В резерве</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c) => (
                <tr key={c.skuId} className="border-t border-on-surface/8">
                  <td className="px-3 py-2">
                    <div className="font-medium">{c.skuLabel}</div>
                    <div className="font-mono text-xs text-on-surface-variant">
                      {c.skuCode}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-destructive">
                    {formatQty(c.writeoffQuantity)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatQty(c.physicalQuantity)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-amber-600">
                    {formatQty(c.reservedQuantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Списание уменьшит остаток, зарезервированный под заказы — заказы могут
          стать необеспеченными.
        </p>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            className="min-h-[56px] w-full sm:w-auto"
          >
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={submitting}
            className="min-h-[56px] w-full sm:w-auto"
          >
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            <span className={submitting ? "ml-2" : ""}>Списать всё равно</span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function QtyCell({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "danger" | "warn";
}) {
  const toneCls =
    tone === "danger"
      ? "text-destructive"
      : tone === "warn"
        ? "text-amber-600"
        : "text-on-surface";
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
        {label}
      </span>
      <span className={`font-mono text-sm ${toneCls}`}>{formatQty(value)}</span>
    </div>
  );
}
